import { useState } from "react";
import { useParams } from "react-router-dom";
import { Download, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { usePageMeta } from "@/hooks/usePageMeta";
import { SummaryView } from "@/components/tshare/SummaryView";

// Public, code-protected view of a member's shared summary
export default function TherapistView() {
  const { token } = useParams();
  usePageMeta({ title: "Shared wellbeing summary · Mindful Me", description: "A private, code-protected wellbeing summary.", path: "/t" });
  const [code, setCode] = useState("");
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const open = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { setD((await api.post(`/public/therapist-share/${token}`, { code })).data); } catch (x) { setErr(errMsg(x)); } finally { setBusy(false); }
  };
  const pdf = async () => {
    try {
      const { data } = await api.post(`/public/therapist-share/${token}/pdf`, { code }, { responseType: "blob" });
      const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(data), download: "wellbeing-summary.pdf" }); a.click();
    } catch { toast.error("This link has expired or was turned off"); }
  };
  if (!d) return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-mm-bg" data-testid="tview-gate">
      <form onSubmit={open} className="mm-card p-8 w-full max-w-sm text-center">
        <Lock className="mx-auto text-mm-primary mb-3" />
        <h1 className="text-2xl font-bold mb-1">Private summary</h1>
        <p className="text-sm text-mm-muted mb-5">Enter the 4-digit code you were given separately.</p>
        <input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 4))} inputMode="numeric" autoFocus placeholder="••••" className="mm-input w-full text-center text-2xl tracking-[0.5em] font-mono" aria-label="Access code" data-testid="tview-code-input" />
        {err && <p className="text-sm text-mm-error mt-3" data-testid="tview-error">{err}</p>}
        <button disabled={code.length !== 4 || busy} className="mm-btn w-full mt-4" data-testid="tview-open">{busy && <Loader2 size={16} className="animate-spin" />}Open summary</button>
      </form>
    </div>
  );
  return (
    <div className="min-h-screen bg-mm-bg p-4 sm:p-8" data-testid="tview-page">
      <div className="max-w-3xl mx-auto mm-card p-6 sm:p-8">
        <div className="flex justify-between items-center gap-3 mb-4">
          <span className="text-xs text-mm-muted">Shared privately · link expires {new Date(d.expires_at).toLocaleDateString()}</span>
          <button onClick={pdf} className="mm-chip" data-testid="tview-pdf"><Download size={14} />PDF</button>
        </div>
        <SummaryView name={d.name} days={d.range_days} sections={d.sections} data={d.data} overview={d.overview} note={d.note} />
      </div>
    </div>
  );
}
